import { EntityId } from '@reduxjs/toolkit';
import { AxiosHeaders } from 'axios';
import { Account } from 'api/account';
import { ApiKey } from 'api/api-keys';
import { constructFormData } from 'api/attachments/construct-form-data';
import { HttpClient } from 'api/http-client';
import {
  Company,
  Team,
  User,
  Session,
  SignupSession,
  UserId,
} from 'api/models';
import { UserPermission } from 'api/permissions';
import {
  UserCreationProps,
  UserProfileCreationProps,
} from 'contexts/project-creation';
import { Project } from '../project';

export interface UserMetaData {
  profileSetupDone?: boolean;
  showProjectsForVisibleDatesRM: boolean;
  startedWithResourceManagement?: boolean;
  ignoreSoftAllocation?: boolean;
  freshchatRestoreId: string;
  /** ISO 8601 formatted date string */
  g2ReviewedAt?: string;
  /** ISO 8601 formatted date string */
  g2SnoozedAt?: string;
  g2Rating?: number;
}

export interface UserMeta {
  userId: number;
  metaData?: UserMetaData;
}

export type BulkOrgChartUpdateData = {
  userId: number;
  managerOf?: number[];
  reportingTo?: number;
};

export type BulkOrgChartUpdate = {
  action: 'ADD' | 'REMOVE' | 'UPDATE';
  data: BulkOrgChartUpdateData[];
};

export type ProjectInUserProfile = Pick<
  Project,
  | 'projectId'
  | 'projectName'
  | 'visibility'
  | 'customer'
  | 'startDate'
  | 'dueDate'
  | 'effortPlanning'
  | 'deleted'
  | 'archived'
>;

export type UserProfileInfo = {
  company: Company;
  user: User;
  userMeta: UserMeta;
  permissions: UserPermission;
  teams: Team[];
  projects: ProjectInUserProfile[];
  projectsIOwn: number[];
  /**
   * @deprecated
   * Do not use this account key. Instead get the object from useAccount();
   */
  account: Pick<Account, 'accountId' | 'accountName'>;
  userId: number;
};

export type AvatarResponse = {
  location: string;
  thumbnailLocation: string;
};

interface BulkUser extends Pick<User, 'emailId' | 'userType'> {
  company: Pick<Company, 'companyId'>;
}

export type BulkUserCreatePayload = BulkUser[];

export interface HubSpotPayload {
  user: Record<EntityId, string | number>;
}

export interface CreateCustomerApiKey {
  jwt: string;
}

export type SupportedLanguage =
  | 'EN'
  | 'FR'
  | 'NL'
  | 'IT'
  | 'DE'
  | 'ES'
  | 'DA'
  | 'NO'
  | 'ZH'
  | 'ZH-HK';

class UserHttpClient extends HttpClient {
  public async getSupportedLanguageList(): Promise<SupportedLanguage[]> {
    return this.get('/users/supported-languages');
  }

  public async getProfile(): Promise<UserProfileInfo> {
    return this.get('/users/profile');
  }

  public async updateMeta(
    payload: Partial<UserMeta>,
  ): Promise<UserProfileInfo> {
    return this.put('/users/meta', payload);
  }

  public async updateMetaDataUser(
    [, userId]: UserId,
    payload: Partial<UserMetaData>,
  ): Promise<UserProfileInfo> {
    return this.put(`/users/${userId}/meta`, payload);
  }

  public async updateCurrentUser(payload: Partial<User>): Promise<User> {
    return this.put('/users', payload);
  }

  public async getAllUsers(): Promise<User[]> {
    return this.get<User[]>('/users');
  }

  public async getUser(userId: number): Promise<User> {
    return this.get(`/users/${userId}`);
  }

  public async getUsersByIds(userIds: number[]): Promise<User[]> {
    const queryString = userIds.join(',');

    return this.get(`/users/bulk?userIds=${queryString}`);
  }

  public async getUsersByEmailIds(userEmailIds: string[]): Promise<User[]> {
    const queryString = userEmailIds.join(',');

    return this.get(`/users/users-by-emails?userEmailIds=${queryString}`);
  }

  public async createUser(payload: UserCreationProps): Promise<User> {
    return this.post('/users', payload);
  }

  public async setupProfileWithToken(
    payload: UserProfileCreationProps,
  ): Promise<SignupSession> {
    return this.post(`/users/signup?token=${payload.token}`, payload);
  }

  public async save(
    payload: AtLeast<User, 'userId'>,
    token?: string,
  ): Promise<User> {
    if (token) {
      return this.put(`/users/${payload.userId}?token=${token}`, payload);
    }

    return this.put(`/users/${payload.userId}`, payload);
  }

  public async getDetailsFromToken(token: string): Promise<Session> {
    return this.post(`/users/auth?token=${token}`);
  }

  public getSession(token: string): Promise<{ sessionToken: string }> {
    return this.get('/users/get-session', {
      params: { token },
      headers: new AxiosHeaders({}),
    });
  }

  public getMagicLink(emailId: string): Promise<{ sessionToken: string }> {
    return this.get('/users/get-magic-link', {
      params: { emailId },
      headers: new AxiosHeaders({}),
    });
  }

  public createCustomerAPiKey(payload: CreateCustomerApiKey): Promise<ApiKey> {
    return this.post('/users/create-customer-api-key-with-token', payload);
  }

  public async changePassword(payload: {
    user: Pick<User, 'userId'>;
    password: {
      oldPassword: string;
      newPassword: string;
      confirmPassword: string;
    };
  }): Promise<User> {
    return this.post(`/users/reset/${payload.user.userId}`, payload.password);
  }

  public async resetPassword(payload: {
    newPassword: string;
    confirmPassword: string;
    token: string;
  }): Promise<{ sessionToken: string }> {
    // TODO: Remove the duplicate token param sending once api fixes it.
    return this.put(`/users/reset?token=${payload.token}`, payload);
  }

  public async forgotPassword(userId: number): Promise<User> {
    return this.post(`/users/forgot/${userId}`);
  }

  public async triggerResetPasswordEmail(email: string): Promise<User> {
    return this.post('/users/forgot', {
      emailId: email,
    });
  }

  public async uploadProfilePic(file: File): Promise<AvatarResponse> {
    const formData = constructFormData(file, {});

    return this.post('/users/photo', formData);
  }

  public async deactivateUser(userId: number): Promise<User> {
    return this.put(`/users/deactivate/${userId}`);
  }

  public async reactivateUser(userId: number): Promise<User> {
    return this.put(`/members/activate/${userId}`); // TODO: /users should slowly migrate to /members
  }

  public async bulkCreate(payload: BulkUserCreatePayload): Promise<User[]> {
    return this.post('/users/bulk-action', payload);
  }

  public bulkUpdateOrgChart = async (
    payload: BulkOrgChartUpdate,
  ): Promise<User[]> => {
    return this.put(
      `/users/bulk-org-tree?action=${payload.action}`,
      payload.data,
    );
  };

  public replaceUserInOrgChart = async (
    oldUser: number,
    newUser: number,
  ): Promise<void> => {
    return this.put(`/users/${oldUser}/replace-user/${newUser}`);
  };

  public async updateHubspot(payload: HubSpotPayload): Promise<void> {
    return this.put('/hubspot/contact', payload);
  }

  public async logout() {
    return this.post('/users/logout');
  }

  public clearOktaSession = async () =>
    this.post('/users/clear-session', undefined, {
      fetchOptions: { credentials: 'same-origin' },
    });

  public getCliToken = async () => {
    return this.get<{ token: string }>('/users/cli-token');
  };

  public getMarketplaceAuthToken = async () => {
    return this.get<{ token: string }>('/users/marketplace-token');
  };
}

export const UserAPI = new UserHttpClient();
